import { DEFAULT_PROMPTBOOK_NAME } from "@/constants";
import { UserMenu } from "@/features/Auth/components/UserMenu/UserMenu";
import { useAppNavigation } from "@/features/Navigation/hooks/useAppNavigation";
import { usePermissions } from "@/features/Permissions/hooks/usePermissions";
import { deletePromptbook } from "@/features/Promptbooks/actions/deletePromptbook";
import { duplicatePromptbook } from "@/features/Promptbooks/actions/duplicatePromptbook";
import { renameResource } from "@/features/Resources/actions/renameResource";
import { useWorkspaceById } from "@/features/Workspaces/hooks/useWorkspaces";
import { goto_workspace_event, save_promptbook_as_template_event } from "@/services/posthog/events";
import { enable_deployment_panel_flag, enable_promptbook_templates_flag } from "@/services/posthog/feature_flags";
import { useAnalytics } from "@/services/posthog/hooks";
import { queryClient } from "@/services/react-query/queryClient";
import { Button, Flex, Spacer, Stack, useToast } from "@chakra-ui/react";
import { Breadcrumb, EditableName, PageContentHeader, PromptStudioLogo } from "@pufflig/prompt-studio-ui";
import { useCreateTemplateFromPromptbook } from "@/features/Templates/actions/createTemplateFromPromptbook";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { disableAutoRename } from "../actions/disableAutoRename";

interface PromptbookPageHeaderProps {
  promptbookId: string;
  promptbookName: string;
  workspaceId: string;
  onOpenDeploymentDrawer: () => void;
}

export const PromptbookEditorHeader = (props: PromptbookPageHeaderProps) => {
  const { promptbookId, workspaceId, promptbookName, onOpenDeploymentDrawer } = props;
  const { data: workspace } = useWorkspaceById(workspaceId);
  const analytics = useAnalytics();
  const canDeploy = useFeatureFlagEnabled(enable_deployment_panel_flag);
  const enablePromptbookTemplates = useFeatureFlagEnabled(enable_promptbook_templates_flag);
  const teamId = workspace?.teamId;
  const { data: permissions } = usePermissions(teamId);
  const canEdit = permissions?.canEditResources || false;
  const createTemplateFromPromptbook = useCreateTemplateFromPromptbook();
  const toast = useToast();

  const { navigateBack, navigateToWorkspace, navigateToPromptbook } = useAppNavigation();

  return (
    <PageContentHeader>
      <Flex width="26px" mr="spacing-md" onClick={navigateBack} cursor={"pointer"}>
        <PromptStudioLogo />
      </Flex>
      <Breadcrumb
        parentName={workspace?.name || ""}
        onParentClick={() => {
          analytics.capture(goto_workspace_event, { from: "breadcrumb" });
          navigateToWorkspace(workspaceId);
        }}
      >
        <EditableName
          canEdit={canEdit}
          name={promptbookName || ""}
          onDelete={async () => {
            await deletePromptbook(promptbookId);
            navigateToWorkspace(workspaceId);
          }}
          onDuplicate={async () => {
            const { newPromptbookId } = await duplicatePromptbook(promptbookId, teamId);
            if (newPromptbookId) navigateToPromptbook(newPromptbookId);
          }}
          onSaveAsTemplate={
            enablePromptbookTemplates
              ? async () => {
                  const { error } = await createTemplateFromPromptbook({
                    promptbookId,
                    teamId,
                  });
                  if (!error) {
                    analytics.capture(save_promptbook_as_template_event, {
                      from: "promptbook_editor_header",
                    });

                    toast({
                      title: "Template created",
                      description: "Your template has been created successfully.",
                      status: "success",
                      duration: 3000,
                      isClosable: true,
                    });
                  }
                }
              : undefined
          }
          onBlur={async (newName) => {
            await renameResource(promptbookId, newName);
            await disableAutoRename(promptbookId);
            queryClient.setQueryData(["promptbooks", promptbookId], {
              ...queryClient.getQueryData(["promptbooks", promptbookId]),
              name: newName || DEFAULT_PROMPTBOOK_NAME,
              auto_rename: false,
            });
          }}
        />
      </Breadcrumb>
      <Spacer />
      <Stack gap="2" direction="row">
        <UserMenu teamId={teamId} />
        {canDeploy && (
          <Button
            variant="primary"
            size="md"
            onClick={() => {
              onOpenDeploymentDrawer();
            }}
          >
            Share
          </Button>
        )}
      </Stack>
    </PageContentHeader>
  );
};
